import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { parse, format, isValid } from 'date-fns';
import { cn } from '@/lib/utils';

// Define our own interface without extending HTML input props
interface DateInputProps {
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
}

/**
 * A custom date input component that automatically formats dates as MM/DD/YYYY
 */
export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  className,
  disabled = false,
  minDate,
}) => {
  const [inputValue, setInputValue] = useState('');
  
  // Format the display value for the input
  useEffect(() => {
    if (value) {
      try {
        // Check if we have a valid ISO date string (YYYY-MM-DD)
        const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        
        if (isoDateMatch) {
          // We have a valid ISO date, extract parts directly
          const [_, year, month, day] = isoDateMatch;
          // Format directly as MM/DD/YYYY (avoiding date object creation)
          const formatted = `${month}/${day}/${year}`;
          setInputValue(formatted);
        } else {
          // Try to parse as a date object (fallback)
          const date = new Date(value);
          if (isValid(date)) {
            const formatted = format(date, 'MM/dd/yyyy');
            setInputValue(formatted);
          } else {
            setInputValue(value);
          }
        }
      } catch (error) {
        console.error('Error parsing initial date value:', error);
        setInputValue(value || '');
      }
    } else {
      setInputValue('');
    }
  }, [value]);

  // Format input as user types
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;
    
    // Allow user to type slashes and handle them properly
    if (input === inputValue) return; // No change
    
    let formatted = '';
    let newCursorPosition = cursorPosition;
    
    // Check if user is manually typing slashes
    if (input.includes('/')) {
      // Process input with manual slashes

      // Handle when user types "8/" -> should become "08/"
      if (/^\d\/$/.test(input)) {
        formatted = `0${input}`;
        newCursorPosition = 3; // Position cursor after the slash
      }
      // Handle when user types "08/2" -> we should NOT auto-pad it with a leading zero
      // Let the user continue typing the day digits
      else if (/^\d{2}\/\d$/.test(input)) {
        formatted = input;
      }
      // Handle when user types "08/25/" -> should become "08/25/"
      else if (/^\d{2}\/\d{2}\/$/.test(input)) {
        formatted = input;
      }
      // Handle when user types "08/25" and wants to add a year - need to automatically add a slash 
      else if (/^\d{2}\/\d{2}$/.test(input)) {
        formatted = input;
      }
      // Handle when user is starting to type a year after MM/DD 
      else if (/^\d{2}\/\d{2}\d{1,4}$/.test(input)) {
        // User typed MM/DD1234 instead of MM/DD/1234, add the missing slash
        const parts = input.match(/^(\d{2})\/(\d{2})(\d{1,4})$/);
        if (parts) {
          formatted = `${parts[1]}/${parts[2]}/${parts[3]}`;
          // If we added a slash, move cursor position if needed
          if (cursorPosition > 5) {
            newCursorPosition = cursorPosition + 1;
          }
        } else {
          formatted = input;
        }
      }
      // Various other formats with slashes
      else {
        formatted = input;
      }
    } else {
      // Strip any non-numeric characters
      const digits = input.replace(/\D/g, '');
      
      // Format as MM/DD/YYYY
      if (digits.length <= 2) {
        // Just the month portion
        formatted = digits;
      } else if (digits.length <= 4) {
        // Month and day portion
        // Add slash after month
        formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        
        // If we just added a slash, update cursor position to account for it
        if (digits.length === 3 && cursorPosition === 3) {
          newCursorPosition = 4;
        }
      } else if (digits.length <= 8) {
        // Complete date with year
        // Add slashes after month and day
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        
        // If we just added a slash, update cursor position to account for it
        if (digits.length === 5 && cursorPosition === 5) {
          newCursorPosition = 6;
        }
      } else {
        // Limit to 8 digits (MM/DD/YYYY)
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
      }
    }
    
    setInputValue(formatted);
    
    // Set cursor position after state update
    setTimeout(() => {
      if (e.target) {
        e.target.selectionStart = newCursorPosition;
        e.target.selectionEnd = newCursorPosition;
      }
    }, 0);
  };

  // When focus is lost, validate and format date
  const handleBlur = () => {
    if (inputValue) {
      try {
        let month = '', day = '', year = '';
        let digits = '';
        
        // Handle input without slashes (e.g. 04252024)
        if (!/\//.test(inputValue) && /^\d+$/.test(inputValue)) {
          digits = inputValue;
          
          // Auto-format as MM/DD/YYYY
          if (digits.length >= 4) {
            month = digits.slice(0, 2);
            day = digits.slice(2, 4);
            year = digits.length > 4 ? digits.slice(4, 8) : new Date().getFullYear().toString();
            
          }
        } 
        // Handle input with slashes
        else {
          // Format: MM/DD/YYYY or MM/DD/YY or variations
          const parts = inputValue.split('/');

          if (parts.length >= 2) {
            month = parts[0].padStart(2, '0');
            day = parts[1].padStart(2, '0');

            // Handle year parsing with intelligent 2-digit year conversion
            if (parts.length > 2 && parts[2]) {
              const yearInput = parts[2];
              if (yearInput.length === 2) {
                // Convert 2-digit year to 4-digit year
                const yearNum = parseInt(yearInput, 10);
                const currentYear = new Date().getFullYear();
                const currentCentury = Math.floor(currentYear / 100) * 100;
                const currentYearInCentury = currentYear % 100;

                // If year is <= current year in century, assume this century
                // If year > current year in century, assume next century (26 -> 2026, 99 -> 2099)
                if (yearNum <= currentYearInCentury + 50) {
                  year = (currentCentury + yearNum).toString();
                } else {
                  year = (currentCentury - 100 + yearNum).toString();
                }
              } else {
                year = yearInput;
              }
            } else {
              year = new Date().getFullYear().toString();
            }

          }
        }
        
        // If we have all parts, validate and format
        if (month && day && year) {
          
          // Validate parts (convert to numbers)
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          const yearNum = parseInt(year, 10);
          
          // Basic range validation
          const isValidMonth = monthNum >= 1 && monthNum <= 12;
          const isValidYear = yearNum >= 1000 && yearNum <= 9999;
          
          // Get days in the specified month
          const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
          const isValidDay = dayNum >= 1 && dayNum <= daysInMonth;
          
          if (isValidMonth && isValidDay && isValidYear) {
            // Format for display (MM/DD/YYYY)
            const formattedDisplay = `${month}/${day}/${year}`;
            setInputValue(formattedDisplay);
            
            // CRITICAL: Don't use date-fns format here to avoid any timezone issues
            // Format directly as YYYY-MM-DD ISO string
            const isoDate = `${year}-${month}-${day}`;
            
            // Also create a JS Date for validation only
            // Use specific date constructor to avoid timezone issues
            const jsDate = new Date(yearNum, monthNum - 1, dayNum);
            
            // Verify that the day matches what we expect (timezone check)
            if (jsDate.getDate() !== dayNum) {
              console.error('Date validation failed: day mismatch', {
                expected: dayNum,
                actual: jsDate.getDate()
              });
            }
            
            // Check minDate constraint
            if (minDate) {
              const minDateString = format(minDate, 'yyyy-MM-dd');
              if (isoDate < minDateString) {
                onChange(minDateString);
                return;
              }
            }
            
            // IMPORTANT: Return the exact ISO date string without any date processing
            onChange(isoDate);
          } else {
            setInputValue(''); // Clear invalid input
            onChange('');
          }
        } else {
          setInputValue(''); // Clear invalid input
          onChange('');
        }
      } catch (error) {
        console.error('Error handling date:', error, inputValue);
        setInputValue(''); // Clear invalid input
        onChange('');
      }
    } else {
      onChange('');
    }
  };

  return (
    <Input
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={cn("date-input", className)}
      disabled={disabled}
    />
  );
};